import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { promoteAssociatedPerson, detectAssociatedPersonAttributes } from '../services/api/personManagementService';
import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
import { listDatasets } from '../services/api/datasetService';
import {
  Gender,
  AgeGroup,
  FacialHair,
  Glasses,
  Height,
  Build,
  SkinTone,
  Dress
} from '@gamma/shared/attributes';
import {
  GenderLabels,
  AgeGroupLabels,
  FacialHairLabels,
  GlassesLabels,
  HeightLabels,
  BuildLabels,
  SkinToneLabels,
  DressLabels
} from '@gamma/shared/labels';

/**
 * AssociatedPersonDialog - Promote associated person to known person
 * Matches PersonDialog.jsx style exactly
 */
export default function AssociatedPersonDialog({ open, associatedPerson, onClose, onPromoted }) {
  const [formData, setFormData] = useState({});
  const [datasets, setDatasets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [touched, setTouched] = useState({});

  const selectProps = { MenuProps: { PaperProps: { sx: { maxHeight: 220 } } } };

  // Load datasets on mount
  useEffect(() => {
    async function fetchDatasets() {
      try {
        const result = await listDatasets({ includeInactive: false });
        setDatasets(result);
      } catch (err) {
        console.error('Failed to load datasets:', err);
      }
    }
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (associatedPerson) {
      setFormData({
        person_id: '',
        first_name: '',
        last_name: '',
        dataset: '',
        gender: '',
        agegroup: '',
        height: '',
        build: '',
        skintone: '',
        eyecolor: '',
        haircolor: '',
        hairstyle: '',
        facialhair: '',
        glasses: '',
        dress: ''
      });
      setTouched({});
    }
  }, [associatedPerson, open]);

  function handleChange(field, value) {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-generate person_id when dataset changes
      if (field === 'dataset' && value && associatedPerson) {
        updated.person_id = `${value}-${associatedPerson.person_id}`;
      }
      return updated;
    });
  }

  async function handleDetectAttributes() {
    if (!associatedPerson) return;

    try {
      setDetecting(true);
      const result = await detectAssociatedPersonAttributes(associatedPerson.person_id);

      // Auto-fill detected attributes
      setFormData((prev) => ({
        ...prev,
        gender: result.attributes.gender || '',
        agegroup: result.attributes.agegroup || '',
        glasses: result.attributes.glasses || '',
        facialhair: result.attributes.facialhair || ''
      }));
    } catch (err) {
      console.error('Failed to detect attributes:', err);
    } finally {
      setDetecting(false);
    }
  }

  async function handlePromote() {
    // Mark all required fields as touched
    setTouched({
      first_name: true,
      last_name: true,
      dataset: true
    });

    // Validation
    if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.dataset?.trim()) {
      return;
    }

    try {
      setSaving(true);
      const result = await promoteAssociatedPerson(associatedPerson.person_id, formData);
      onPromoted(result.person);
    } catch (err) {
      console.error('Failed to promote:', err);
    } finally {
      setSaving(false);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return `${date.getDate()} ${date.toLocaleDateString('en-GB', { month: 'short' })} ${date.getFullYear()}`;
  }

  if (!associatedPerson) return null;

  // Get reference image (best quality sighting)
  const bestSighting = associatedPerson.sightings?.[0];
  const referenceImage = bestSighting?.cropped_face_s3_key || bestSighting?.capture_s3_key;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { height: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {referenceImage && (
            <Box
              component="img"
              src={getCapturedImageDisplayUrl(referenceImage)}
              alt="Associated Person"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                objectFit: 'cover'
              }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
              Promote to Known Person
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              ID: {associatedPerson.person_id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Sightings: <strong>{associatedPerson.sighting_count || 0}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              First: {formatDate(associatedPerson.first_seen)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Last: {formatDate(associatedPerson.last_seen)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1, pb: 1 }}>
        <Grid container spacing={1.2}>
          {/* Identity Section */}
          <Grid item xs={12}>
            <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.5 }}>
              Identity
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Dataset"
              value={formData.dataset || ''}
              onChange={(e) => handleChange('dataset', e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, dataset: true }))}
              required
              error={touched.dataset && !formData.dataset}
              size="small"
              SelectProps={selectProps}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              helperText="Dataset to assign this person to"
            >
              <MenuItem value="">Select Dataset</MenuItem>
              {datasets.map((dataset) => (
                <MenuItem key={dataset.dataset_id} value={dataset.dataset_id}>
                  {dataset.dataset_name} ({dataset.dataset_id})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Person ID (Auto-generated)"
              value={formData.person_id || ''}
              size="small"
              disabled
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              helperText="Format: {DATASET}-{ASSOCIATED_ID}"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name || ''}
              onChange={(e) => handleChange('first_name', e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, first_name: true }))}
              required
              error={touched.first_name && !formData.first_name?.trim()}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name || ''}
              onChange={(e) => handleChange('last_name', e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, last_name: true }))}
              required
              error={touched.last_name && !formData.last_name?.trim()}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          {/* Physical Characteristics Section */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.5 }}>
                Physical Characteristics
              </Typography>
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={handleDetectAttributes}
                disabled={detecting}
                sx={{ fontSize: '0.7rem', py: 0.3 }}
              >
                {detecting ? 'Detecting...' : 'Auto-Detect'}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Gender"
              value={formData.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              {...selectProps}
            >
              <MenuItem value="">Unknown</MenuItem>
              <MenuItem value={Gender.MALE}>{GenderLabels[Gender.MALE]}</MenuItem>
              <MenuItem value={Gender.FEMALE}>{GenderLabels[Gender.FEMALE]}</MenuItem>
              <MenuItem value={Gender.OTHER}>{GenderLabels[Gender.OTHER]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Age Group"
              value={formData.agegroup || ''}
              onChange={(e) => handleChange('agegroup', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              {...selectProps}
            >
              <MenuItem value="">Unknown</MenuItem>
              <MenuItem value={AgeGroup.YOUNG}>{AgeGroupLabels[AgeGroup.YOUNG]}</MenuItem>
              <MenuItem value={AgeGroup.THIRTIES}>{AgeGroupLabels[AgeGroup.THIRTIES]}</MenuItem>
              <MenuItem value={AgeGroup.FORTIES}>{AgeGroupLabels[AgeGroup.FORTIES]}</MenuItem>
              <MenuItem value={AgeGroup.FIFTIES}>{AgeGroupLabels[AgeGroup.FIFTIES]}</MenuItem>
              <MenuItem value={AgeGroup.SIXTIES}>{AgeGroupLabels[AgeGroup.SIXTIES]}</MenuItem>
              <MenuItem value={AgeGroup.SEVENTYPLUS}>{AgeGroupLabels[AgeGroup.SEVENTYPLUS]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Glasses"
              value={formData.glasses || ''}
              onChange={(e) => handleChange('glasses', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              {...selectProps}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value={Glasses.YES}>{GlassesLabels[Glasses.YES]}</MenuItem>
              <MenuItem value={Glasses.NO}>{GlassesLabels[Glasses.NO]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Facial Hair"
              value={formData.facialhair || ''}
              onChange={(e) => handleChange('facialhair', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              {...selectProps}
            >
              <MenuItem value="">Unknown</MenuItem>
              <MenuItem value={FacialHair.NONE}>{FacialHairLabels[FacialHair.NONE]}</MenuItem>
              <MenuItem value={FacialHair.MOUSTACHE}>{FacialHairLabels[FacialHair.MOUSTACHE]}</MenuItem>
              <MenuItem value={FacialHair.BEARD}>{FacialHairLabels[FacialHair.BEARD]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              select
              label="Height"
              value={formData.height || ''}
              onChange={(e) => handleChange('height', e.target.value)}
              size="small"
              SelectProps={selectProps}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value={Height.SHORT}>{HeightLabels[Height.SHORT]}</MenuItem>
              <MenuItem value={Height.AVG}>{HeightLabels[Height.AVG]}</MenuItem>
              <MenuItem value={Height.TALL}>{HeightLabels[Height.TALL]}</MenuItem>
              <MenuItem value={Height.GIANT}>{HeightLabels[Height.GIANT]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              select
              label="Build"
              value={formData.build || ''}
              onChange={(e) => handleChange('build', e.target.value)}
              size="small"
              SelectProps={selectProps}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value={Build.SKINNY}>{BuildLabels[Build.SKINNY]}</MenuItem>
              <MenuItem value={Build.SLIM}>{BuildLabels[Build.SLIM]}</MenuItem>
              <MenuItem value={Build.AVG}>{BuildLabels[Build.AVG]}</MenuItem>
              <MenuItem value={Build.TUBBY}>{BuildLabels[Build.TUBBY]}</MenuItem>
              <MenuItem value={Build.FAT}>{BuildLabels[Build.FAT]}</MenuItem>
              <MenuItem value={Build.HUGE}>{BuildLabels[Build.HUGE]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              select
              label="Skin Tone"
              value={formData.skintone || ''}
              onChange={(e) => handleChange('skintone', e.target.value)}
              size="small"
              SelectProps={selectProps}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value={SkinTone.WHITE}>{SkinToneLabels[SkinTone.WHITE]}</MenuItem>
              <MenuItem value={SkinTone.ASIAN}>{SkinToneLabels[SkinTone.ASIAN]}</MenuItem>
              <MenuItem value={SkinTone.BLACK}>{SkinToneLabels[SkinTone.BLACK]}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              label="Eye Color"
              value={formData.eyecolor || ''}
              onChange={(e) => handleChange('eyecolor', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              label="Hair Color"
              value={formData.haircolor || ''}
              onChange={(e) => handleChange('haircolor', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              label="Hair Style"
              value={formData.hairstyle || ''}
              onChange={(e) => handleChange('hairstyle', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              select
              label="Dress"
              value={formData.dress || ''}
              onChange={(e) => handleChange('dress', e.target.value)}
              size="small"
              SelectProps={selectProps}
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value={Dress.FORMAL}>{DressLabels[Dress.FORMAL]}</MenuItem>
              <MenuItem value={Dress.BUSINESS}>{DressLabels[Dress.BUSINESS]}</MenuItem>
              <MenuItem value={Dress.CASUAL}>{DressLabels[Dress.CASUAL]}</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ fontSize: '0.85rem' }}>
          Cancel
        </Button>
        <Button
          onClick={handlePromote}
          variant="contained"
          disabled={saving}
          sx={{ fontSize: '0.85rem' }}
        >
          {saving ? 'Promoting...' : 'Promote to Known Person'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
